import { Icon, MenuGroupContent, MenuOption } from "@smartsuite/react-ui/lib";
import { IntegrationLabelStyled } from "../style";
import { translateIfPresent } from "../../../../../../locales/i18n";
import type { InputConfig } from "@smartsuite/automation-engine-api";
import { useCallback, useMemo } from "react";
import type { ReactElement } from "react";
import type { ActionCandidate } from "../../../../../../types/candidate";
import { StyledOptionLabel } from "./style";
import { SolutionColorVariables } from "@smartsuite/types";

export const InputNestedFieldOptions: React.FC<{
  options: InputConfig[];
  action: ActionCandidate;
  onClickMenu: (inputDesc: InputConfig) => void;
  onNavigateBack: () => void;
  subMenuTitle: string;
}> = ({ options, action, onClickMenu, onNavigateBack, subMenuTitle }) => {
  const findLabel = useCallback(
    (input: InputConfig): { label: string; suffix: string } => {
      const description = action.description.inputs.find(
        (desc) => desc.inputId === input.inputId,
      );

      if (description !== undefined) {
        const isEntireField = description.labelSuffix === "Entire Field";
        const pillTitle =
          description.labelSuffix != null && !isEntireField
            ? `${description.label}: ${description.labelSuffix}`
            : description.label;

        return { label: pillTitle, suffix: description.labelSuffix ?? "" };
      }

      return { label: "", suffix: "" };
    },
    [action.description.inputs],
  );

  const shouldShowEntireFieldSuffix = useCallback(
    (_input: InputConfig, suffix: string): boolean => {
      return suffix === "" || suffix === "Entire Field";
    },
    [],
  );

  const getFieldIcon = useMemo(
    () =>
      (input: InputConfig): ReactElement => {
        const description = action.description.inputs.find(
          (item) => item.inputId === input.inputId,
        );
        return (
          <Icon
            size="S"
            name={description?.icon}
            color={SolutionColorVariables.sc1}
          />
        );
      },
    [action.description.inputs],
  );

  const onOptionClick = (inputFieldGroup: InputConfig): void => {
    onClickMenu(inputFieldGroup);
  };

  return (
    <MenuGroupContent
      title={subMenuTitle}
      search={false}
      maxHeight={400}
      smallListPaddings={true}
      onNavigateBack={onNavigateBack}
    >
      {options
        .map((input) => {
          const { label, suffix } = findLabel(input);
          return { label, input, suffix };
        })
        .sort((a, b) => a.label.localeCompare(b.label))
        .map(({ label, input, suffix }, index) => {
          const icon = getFieldIcon(input);
          const hasIcon = !!icon.props.name;

          return (
            <MenuOption
              key={`field-option-${action.hash}-${input.inputId}-${index}`}
              renderIcon={() => (hasIcon ? icon : <></>)}
              onClick={(e) => {
                e.stopPropagation();
                onOptionClick(input);
              }}
            >
              <IntegrationLabelStyled paddingLeft={!hasIcon ? "0" : undefined}>
                {translateIfPresent(`caption.${label}`, label)}
                {shouldShowEntireFieldSuffix(input, suffix) ? (
                  <StyledOptionLabel> • Entire Field</StyledOptionLabel>
                ) : (
                  ""
                )}
              </IntegrationLabelStyled>
            </MenuOption>
          );
        })}
    </MenuGroupContent>
  );
};
